import store from '@/store';
import { ref, reactive, onBeforeMount, watch } from '@vue/composition-api';

import { articleCategoryModel } from '@/models/article/article';
import useDate from '@/utils/hooks/useDate';
import usePagination from '@/views/components/pagination/usePagination';
import useValidationModal from '@/@core/components/base-modal/hooks/useValidationModal';
import useFilterHeaderTable from '@/views/components/filter-header-table/useFilterHeaderTable';

import usePermissionByAccount from "@/constants/navigation/BOYNYKJMS2EH/permission-by-account"

export default function useArticleCategory() {
    const { getPermissionModule } = usePermissionByAccount()
    const { fetchArticleCategoryListModel, categorySectionSort, deleteCategoryModel, editArticleCategoryModel } = articleCategoryModel();
    const { pagination } = usePagination();
    const { resolveDateWithSeperator } = useDate();
    const { openDefaultErrorModal, openSuccessModal, openWarningModal } = useValidationModal();
    const tableModeReorder = ref(false);
    const keyChange = ref(0);

    const {
        filters,
        searchValue,
        ordering,
        onSearch,
        sortingChanged,
        onSelectFilter,
    } = useFilterHeaderTable();

    const fields = [
        { key: 'seq', label: 'No.' },
        { key: 'category_active_img', label: 'Icon (Active)' },
        { key: 'category_inactive_img', label: 'Icon (Inactive)' },
        { key: 'category_name_th', label: 'Category', sortable: true },
        { key: 'valid_from', label: 'Valid From' },
        { key: 'valid_through', label: 'Valid Through' },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'actions', label: '' }
    ];

    const loading = reactive({
        tableData: false,
        api: false
    });

    const articleCategory = reactive({
        payload: []
    });

    const statusOption = ref([
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 0 }
    ]);

    const fetchArticleCategory = (loadingAction = true) => {
        if (loadingAction) {
            loading.tableData = true;
        }
        fetchArticleCategoryListModel.payload.Page = pagination.currentPage;
        fetchArticleCategoryListModel.payload.Limit = pagination.perPage;
        fetchArticleCategoryListModel.payload.Filters = filters;
        fetchArticleCategoryListModel.payload.Ordering = ordering.value;

        store
            .dispatch('api/apiRequest', fetchArticleCategoryListModel)
            .then((response) => {
                articleCategory.payload = response.data.payload;
                pagination.rows = response.data.payload.length;
                pagination.totalRows = response.data.filtered_record;
            })
            .catch((err) => {
                openDefaultErrorModal(err)
            })
            .finally(() => {
                loading.tableData = false;
            })
    };

    const onToggleStatus = (status, category) => {
        editArticleCategoryModel.payload.Seq = category.seq;
        editArticleCategoryModel.payload.Category_Id = category.category_id;
        editArticleCategoryModel.payload.Category_Name_TH = category.category_name_th;
        editArticleCategoryModel.payload.Category_Name_EN = category.category_name_en;
        editArticleCategoryModel.payload.Category_Active_Img = category.category_active_img;
        editArticleCategoryModel.payload.Category_Inactive_Img = category.category_inactive_img;
        editArticleCategoryModel.payload.Valid_From = category.valid_from;
        editArticleCategoryModel.payload.Valid_Through = category.valid_through;
        editArticleCategoryModel.payload.Status = status ? 1 : 0;

        store
            .dispatch('api/apiFormRequest', editArticleCategoryModel)
            .catch((err) => openDefaultErrorModal(err))
            .finally(() => {
                editArticleCategoryModel.payload = {
                    Category_Id: null,
                    Seq: null,
                    Category_Name_TH: null,
                    Category_Name_EN: null,
                    Category_Active_Img: null,
                    Category_Inactive_Img: null,
                    Valid_From: null,
                    Valid_Through: null,
                    Status: null,
                };

                fetchArticleCategory(false);
            });
    };

    const deleteCategory = (catgoryId) => {
        deleteCategoryModel.payload.Category_Id = catgoryId;

        store
            .dispatch('api/apiRequest', deleteCategoryModel)
            .then((response) => {
                openSuccessModal(
                    'ทำรายการสำเร็จ !',
                    null,
                    'ตกลง',
                    () => fetchArticleCategory(false),
                    () => fetchArticleCategory(false)
                )
            })
            .catch((err) => {
                openDefaultErrorModal(err)
            })
    };

    const handleDeleteModal = (id) => {
        openWarningModal(
            'ลบ Category',
            `ต้องการลบ Category นี้ใช่หรือไม่`,
            () => {
                deleteCategory(id);
            }
        )
    };

    const onFilterEntity = (numOfEntity) => {
        pagination.perPage = numOfEntity;
    };

    const onPageChange = (page) => {
        pagination.currentPage = page;
    };

    const onSearchCategory = (input) => {
        searchValue.query = input;
        onSearch('category_name_th');
    };

    const handleTableReorder = () => {
        tableModeReorder.value = !tableModeReorder.value;
        sortableRowOptions.value.disabled = !tableModeReorder.value
    };

    // const arrayMove = (arr, old_index, new_index) => {
    //     if (new_index >= arr.length) {
    //         var k = new_index - arr.length + 1;
    //         while (k--) {
    //             arr.push(undefined);
    //         }
    //     }
    //     arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    //     return arr;
    // };
    const fetchCategoryReorder = (categoryId, currentPostion, newPosition) => {
        loading.tableData = true;
        categorySectionSort.payload.Category_Id = categoryId;
        categorySectionSort.payload.FromSeq = currentPostion;
        categorySectionSort.payload.ToSeq = parseInt(newPosition);

        store
            .dispatch('api/apiRequest', categorySectionSort)
            .then(response => {
                fetchArticleCategory(false);
            })
            .catch((err) => {
                openDefaultErrorModal(err)
                loading.tableData = false;
            })
    }

    const handleRowChange = (event) => {
        fetchCategoryReorder(
            articleCategory.payload[event.oldDraggableIndex].category_id,
            articleCategory.payload[event.oldDraggableIndex].seq,
            articleCategory.payload[event.newDraggableIndex].seq,
        )
        articleCategory.payload.splice(
            event.newDraggableIndex,
            0,
            articleCategory.payload.splice(event.oldDraggableIndex, 1)[0]
        );
        keyChange.value++;
    };

    const sortableRowOptions = ref({
        chosenClass: 'is-selected',
        ghostClass: 'sortable-ghost',
        onEnd: handleRowChange,
        disabled: true,
        animation: 300,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    });

    onBeforeMount(() => {
        fetchArticleCategory();
    });

    watch(
        pagination,
        () => {
            if (!loading.tableData) {
                fetchArticleCategory();
            }
        },
        { deep: true }
    );

    watch(
        [filters, ordering],
        () => {
            pagination.currentPage = 1;

            fetchArticleCategory();
        },
        { deep: true }
    );

    return {
        fields,
        loading,
        keyChange,
        articleCategory,
        statusOption,
        sortableRowOptions,
        tableModeReorder,
        pagination,
        ordering,
        onSelectFilter,
        onPageChange,
        onSearchCategory,
        onFilterEntity,
        sortingChanged,
        resolveDateWithSeperator,
        handleRowChange,
        handleTableReorder,
        handleDeleteModal,
        onToggleStatus,
        getPermissionModule
    }
}